import { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { mcpAPI } from '../UserAPI';
import { configuration } from '../Configuration';
import z from 'zod';

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";

interface MCPTestDialogProps {
    isOpen: boolean;
    onDismiss: () => void;
    serverURL: string;
}

interface MCPTool {
    name: string;
    title?: string; // Some MCP servers use 'title' instead of or in addition to 'name'
    description?: string;
    inputSchema?: any; // Can be Zod schema or JSON schema
    outputSchema?: any;
}

interface ParameterField {
    name: string;
    type: string;
    required: boolean;
    description?: string;
    value: any;
}

interface ToolOption {
    key: string;
    text: string;
    data: MCPTool;
}

export default function MCPTestDialog({ isOpen, onDismiss, serverURL }: MCPTestDialogProps) {
    const { t } = useTranslation();
    const [tools, setTools] = useState<MCPTool[]>([]);
    const [selectedTool, setSelectedTool] = useState<MCPTool | null>(null);
    const [parameters, setParameters] = useState<ParameterField[]>([]);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [mcpClient, setMcpClient] = useState<any | null>(null); // Changed to any as MCPClient is no longer used
    const [transformedURL, setTransformedURL] = useState<string | null>(null);
    const [executionTime, setExecutionTime] = useState<number>(0);

    // Connect to MCP server and list tools
    useEffect(() => {
        if (isOpen && serverURL) {
            connectToServer();
        }
    }, [isOpen, serverURL]);



    const connectToServer = async () => {
        setConnecting(true);
        setError(null);
        setSuccess(null);
        setTools([]);
        setSelectedTool(null);
        setParameters([]);
        setResult(null);
        setTransformedURL(null);

        try {
            // Transform URL for test mode or use proxy for external servers
            let transformedURL = serverURL;
            if (configuration.mode === 'test' && serverURL.startsWith('https://api.the-real-insight.ai')) {
                transformedURL = serverURL.replace('https://api.the-real-insight.ai', configuration.serverUrl);
                console.log(`Testmodus: URL von ${serverURL} zu ${transformedURL} transformiert`);
                setTransformedURL(transformedURL);
            } else if (serverURL.startsWith('http') && !serverURL.includes('localhost')) {
                // Use proxy for external MCP servers to avoid CORS issues
                // The backend proxy handles CORS headers and forwards requests to the actual MCP server
                const proxyUrl = `${configuration.serverUrl}/mcp-proxy`;
                console.log(`Configuration server URL: ${configuration.serverUrl}`);
                console.log(`Using proxy for external MCP server: ${serverURL} -> ${proxyUrl}`);
                transformedURL = proxyUrl;
                setTransformedURL(proxyUrl);
            } else {
                setTransformedURL(null);
            }

            // Create authenticated MCP client with the original server URL as target
            const client = mcpAPI.createClient(serverURL);
            try {
                // Try the standard connection first
                await client.connect();

                setMcpClient(client);
            } catch (error) {
                console.error('AuthenticatedMCPClient-Verbindung fehlgeschlagen:', error);
                throw error;
            }

            const toolsList = await client.listTools();

            // The listTools method should now return the tools array directly
            let actualTools = toolsList;

            // If toolsList is not an array, it might be an error response or unexpected format
            if (toolsList && typeof toolsList === 'object' && !Array.isArray(toolsList)) {
                if (toolsList.tools && Array.isArray(toolsList.tools)) {
                    actualTools = toolsList.tools;
                } else if (toolsList.data && Array.isArray(toolsList.data)) {
                    actualTools = toolsList.data;
                } else {
                    // If it's an object but doesn't have tools or data arrays, it might be an error response
                    actualTools = [];
                }
            }

            if (!actualTools || actualTools.length === 0) {
                setError(t('ui.connectedSuccessfullyNoTools'));
            } else {
                setSuccess(t('ui.connectedSuccessfullyToolsFound', { count: actualTools.length }));
                console.log('Setting tools state with:', actualTools);
                setTools(actualTools);
            }
        } catch (err: any) {
            console.error('Verbindungsfehler-Details:', err);
            let errorMessage = t('ui.connectionFailed', { message: err.message || err });

            // Detailliertere Fehlermeldungen für häufige Probleme
            if (err.message && err.message.includes('CORS')) {
                errorMessage = t('ui.corsError');
            } else if (err.message && err.message.includes('404')) {
                errorMessage = t('ui.notFoundError');
            } else if (err.message && err.message.includes('401')) {
                errorMessage = t('ui.unauthorizedError');
            } else if (err.message && err.message.includes('403')) {
                errorMessage = t('ui.forbiddenError');
            } else if (err.message && err.message.includes('timeout')) {
                errorMessage = t('ui.timeoutError');
            } else if (err.message && err.message.includes('Cannot POST')) {
                errorMessage = t('ui.invalidEndpointError');
            }

            setError(errorMessage + '\n\n' + t('ui.mcpHint'));
        } finally {
            setConnecting(false);
        }
    };

    // Convert Zod schema to JSON schema
    const convertZodToJsonSchema = (zodSchema: any): any => {
        if (!zodSchema || !zodSchema.def) {
            return { type: 'object', properties: {} };
        }
        return z.toJSONSchema(zodSchema);
    };

    // Handle tool selection
    const handleToolSelect = (tool: MCPTool) => {
        console.log('Selected tool:', tool);
        setSelectedTool(tool);
        setResult(null);
        setError(null);
        setSuccess(null);

        // Parse input schema to create parameter fields
        if (tool.inputSchema) {
            const fields: ParameterField[] = [];

            // Convert Zod schema to JSON schema if needed
            let jsonSchema = tool.inputSchema;
            console.log('Original input schema:', tool.inputSchema);
            if (tool.inputSchema.def) {
                // This is a Zod schema, convert it
                jsonSchema = convertZodToJsonSchema(tool.inputSchema);
                console.log('Converted JSON schema:', jsonSchema);
            }

            const properties = jsonSchema.properties || {};
            const required = jsonSchema.required || [];

            Object.keys(properties).forEach(propName => {
                const prop = properties[propName];
                let defaultValue = prop.default;

                // Set appropriate default values based on type
                if (defaultValue === undefined) {
                    switch (prop.type) {
                        case 'boolean':
                            defaultValue = false;
                            break;
                        case 'number':
                        case 'integer':
                            defaultValue = 0;
                            break;
                        case 'array':
                            defaultValue = [];
                            break;
                        case 'object':
                            defaultValue = {};
                            break;
                        default:
                            defaultValue = '';
                    }
                }

                fields.push({
                    name: propName,
                    type: prop.type || 'string',
                    required: required.includes(propName),
                    description: prop.description,
                    value: defaultValue
                });
            });

            setParameters(fields);
        } else {
            setParameters([]);
        }
    };

    // Handle parameter value change
    const handleParameterChange = (name: string, value: any) => {
        setParameters(prev => prev.map(param =>
            param.name === name ? { ...param, value } : param
        ));
    };

    // Validate required parameters
    const validateParameters = () => {
        const missingRequired = parameters.filter(param =>
            param.required && (param.value === '' || param.value === null || param.value === undefined)
        );

        if (missingRequired.length > 0) {
            setError(`Fehlende erforderliche Parameter: ${missingRequired.map(p => p.name).join(', ')}`);
            return false;
        }
        return true;
    };

    // Execute tool
    const executeTool = async () => {
        if (!selectedTool || !mcpClient) return;

        if (!validateParameters()) {
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);
        setExecutionTime(0);

        const startTime = Date.now();

        // Update execution time every second
        const timeInterval = setInterval(() => {
            setExecutionTime(Math.floor((Date.now() - startTime) / 1000));
        }, 1000);

        try {
            // Build arguments object from parameters
            const args: Record<string, any> = {};
            parameters.forEach(param => {
                if (param.value !== '' && param.value !== null && param.value !== undefined) {
                    // Convert value based on type
                    let convertedValue = param.value;
                    if (param.type === 'number' || param.type === 'integer') {
                        convertedValue = Number(param.value);
                    } else if (param.type === 'boolean') {
                        convertedValue = Boolean(param.value);
                    } else if (param.type === 'object') {
                        // If it's still a string (invalid JSON), try to parse it
                        if (typeof param.value === 'string') {
                            try {
                                convertedValue = JSON.parse(param.value);
                            } catch (err) {
                                // If parsing fails, show error and don't include this parameter
                                throw new Error(`Ungültiges JSON für Parameter "${param.name}": ${param.value}`);
                            }
                        }
                        // Otherwise, it's already an object, use it as is
                    }
                    args[param.name] = convertedValue;
                }
            });

            // Call the tool using our AuthenticatedMCPClient's callTool method
            let toolResult;
            try {
                toolResult = await mcpClient.callTool(selectedTool.name, args);
            } catch (err: any) {
                throw new Error(`Werkzeug-Ausführung fehlgeschlagen: ${err.message || err}`);
            }
            setResult(toolResult);
        } catch (err: any) {
            setError(`Werkzeug-Ausführung fehlgeschlagen: ${err.message || err}`);
        } finally {
            clearInterval(timeInterval);
            setLoading(false);
            setExecutionTime(0);
        }
    };

    // Cleanup on dialog close
    const handleDismiss = () => {
        if (mcpClient) {
            // The mcpAPI.disconnect() method is not directly available in the new UserAPI.
            // If a disconnect method is needed, it should be added to the UserAPI class.
            // For now, we'll just dismiss the dialog.
        }
        onDismiss();
    };

    const toolOptions: ToolOption[] = tools.map(tool => ({
        key: tool.name,
        text: tool.title ? `${tool.title} - ${tool.description || ''}` : (tool.description ? `${tool.name} - ${tool.description}` : tool.name),
        data: tool
    }));

    const renderParameterField = (param: ParameterField) => {
        const fieldId = `mcp-param-${param.name}`;
        const fieldLabel = (
            <Label htmlFor={fieldId}>
                {param.name}
                {param.required && <span className="ml-0.5 text-destructive">*</span>}
            </Label>
        );

        switch (param.type) {
            case 'boolean':
                return (
                    <div className="flex flex-col gap-1.5">
                        {fieldLabel}
                        <Select
                            value={param.value ? 'true' : 'false'}
                            onValueChange={(value) => handleParameterChange(param.name, value === 'true')}
                        >
                            <SelectTrigger id={fieldId}>
                                <SelectValue placeholder={param.description} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="true">True</SelectItem>
                                <SelectItem value="false">False</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                );
            case 'number':
            case 'integer':
                return (
                    <div className="flex flex-col gap-1.5">
                        {fieldLabel}
                        <Input
                            id={fieldId}
                            placeholder={param.description}
                            type="number"
                            value={param.value?.toString() || ''}
                            onChange={(event) => handleParameterChange(param.name, event.target.value)}
                        />
                    </div>
                );
            case 'array':
                return (
                    <div className="flex flex-col gap-1.5">
                        {fieldLabel}
                        <Textarea
                            id={fieldId}
                            className="resize-none"
                            placeholder={`${param.description || 'Als JSON-Array eingeben, z.B. ["item1", "item2"]'}`}
                            value={param.value?.toString() || ''}
                            onChange={(event) => {
                                const value = event.target.value;
                                try {
                                    if (value) {
                                        const parsed = JSON.parse(value);
                                        handleParameterChange(param.name, parsed);
                                    } else {
                                        handleParameterChange(param.name, []);
                                    }
                                } catch (err) {
                                    // Keep as string if not valid JSON
                                    handleParameterChange(param.name, value);
                                }
                            }}
                            rows={3}
                        />
                    </div>
                );
            case 'object':
                // For object types, allow value to be either an object (parsed) or a string (while editing)
                const displayValue = typeof param.value === 'object' && param.value !== null
                    ? JSON.stringify(param.value, null, 2)
                    : (typeof param.value === 'string' ? param.value : '{}');
                return (
                    <div className="flex flex-col gap-1.5">
                        {fieldLabel}
                        <Textarea
                            id={fieldId}
                            className="resize-none"
                            placeholder={`${param.description || 'Als JSON-Objekt eingeben, z.B. {"key": "value"}'}`}
                            value={displayValue}
                            onChange={(event) => {
                                const value: string | null | undefined = event.target.value;
                                if (value === undefined || value === null) {
                                    handleParameterChange(param.name, {});
                                    return;
                                }

                                const trimmedValue = value.trim();
                                if (!trimmedValue) {
                                    handleParameterChange(param.name, {});
                                    return;
                                }

                                try {
                                    const parsed = JSON.parse(trimmedValue);
                                    handleParameterChange(param.name, parsed);
                                } catch (err) {
                                    // While user is typing invalid JSON, store as string temporarily
                                    // This allows the field to show what they're typing
                                    handleParameterChange(param.name, trimmedValue);
                                }
                            }}
                            rows={5}
                        />
                    </div>
                );
            default:
                return param.type === 'string' && (param.description?.includes('text') || param.description?.includes('content') || param.description?.includes('description')) ? (
                    <div className="flex flex-col gap-1.5">
                        {fieldLabel}
                        <Textarea
                            id={fieldId}
                            className="resize-none"
                            placeholder={param.description}
                            value={param.value?.toString() || ''}
                            onChange={(event) => handleParameterChange(param.name, event.target.value)}
                            rows={3}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col gap-1.5">
                        {fieldLabel}
                        <Input
                            id={fieldId}
                            placeholder={param.description}
                            value={param.value?.toString() || ''}
                            onChange={(event) => handleParameterChange(param.name, event.target.value)}
                        />
                    </div>
                );
        }
    };

    const extractUnstructuredContent = (data: any): string => {
        if (typeof data === 'string') {
            return data;
        }

        if (typeof data === 'object' && data !== null) {
            let formattedContent = '';

            // Debug logging to see the actual data structure
            console.log('extractUnstructuredContent - Input data:', JSON.stringify(data, null, 2));

            // Check all possible content locations
            let contentFound = false;

            // 1. Check for multiple messages structure (new MCP proxy format)
            if (data.messages && Array.isArray(data.messages)) {
                console.log('Found messages array:', data.messages);
                const allContent: string[] = [];

                data.messages.forEach((message: any, index: number) => {
                    if (message.params && message.params.data) {
                        console.log(`Found message ${index} with params.data:`, message.params.data);
                        contentFound = true;
                        allContent.push(message.params.data);
                    } else if (message.result && message.result.content) {
                        console.log(`Found message ${index} with result.content:`, message.result.content);
                        contentFound = true;
                        if (Array.isArray(message.result.content)) {
                            const contentItems = message.result.content.map((item: any) => {
                                if (item.type === 'text' && item.text) {
                                    return item.text;
                                } else if (typeof item === 'string') {
                                    return item;
                                }
                                return '';
                            }).filter(Boolean);
                            if (contentItems.length > 0) {
                                allContent.push(contentItems.join('\n\n'));
                            }
                        }
                    } else if (typeof message === 'string') {
                        contentFound = true;
                        allContent.push(message);
                    } else if (typeof message === 'object') {
                        const nestedContent = extractUnstructuredContent(message);
                        if (nestedContent && nestedContent !== '**Keine unstrukturierten Daten gefunden**') {
                            contentFound = true;
                            allContent.push(nestedContent);
                        }
                    }
                });

                if (allContent.length > 0) {
                    console.log('Returning all content from messages:', allContent);
                    return allContent.join('\n\n---\n\n');
                }
            }

            // 2. Check for notifications array (new MCP proxy format)
            if (data.notifications && Array.isArray(data.notifications)) {
                console.log('Found notifications array:', data.notifications);
                const allContent: string[] = [];

                data.notifications.forEach((notification: any, index: number) => {
                    if (notification.params && notification.params.data) {
                        console.log(`Found notification ${index} with params.data:`, notification.params.data);
                        contentFound = true;
                        allContent.push(notification.params.data);
                    }
                });

                if (allContent.length > 0) {
                    console.log('Returning all content from notifications:', allContent);
                    return allContent.join('\n\n---\n\n');
                }
            }

            // 3. Check for result.content array (MCP server response structure)
            if (data.result && data.result.content && Array.isArray(data.result.content)) {
                console.log('Found result.content array:', data.result.content);
                const contentItems = data.result.content.map((item: any) => {
                    if (item.type === 'text' && item.text) {
                        console.log('Found text item in result.content:', item.text);
                        contentFound = true;
                        return item.text;
                    } else if (typeof item === 'string') {
                        contentFound = true;
                        return item;
                    } else if (typeof item === 'object' && item !== null) {
                        const nestedContent = extractUnstructuredContent(item);
                        if (nestedContent) {
                            contentFound = true;
                            return nestedContent;
                        }
                    }
                    return '';
                }).filter(Boolean);

                if (contentItems.length > 0) {
                    console.log('Returning contentItems from result.content:', contentItems);
                    return contentItems.join('\n\n');
                }
            }

            // 4. Check for direct content array
            if (data.content && Array.isArray(data.content)) {
                console.log('Found direct content array:', data.content);
                const contentItems = data.content.map((item: any) => {
                    if (item.type === 'text' && item.text) {
                        console.log('Found text item in direct content:', item.text);
                        contentFound = true;
                        return item.text;
                    } else if (typeof item === 'string') {
                        contentFound = true;
                        return item;
                    } else if (typeof item === 'object' && item !== null) {
                        const nestedContent = extractUnstructuredContent(item);
                        if (nestedContent) {
                            contentFound = true;
                            return nestedContent;
                        }
                    }
                    return '';
                }).filter(Boolean);

                if (contentItems.length > 0) {
                    console.log('Returning contentItems from direct content:', contentItems);
                    return contentItems.join('\n\n');
                }
            }

            // 5. Check for result as string
            if (data.result && typeof data.result === 'string') {
                console.log('Found string result:', data.result);
                contentFound = true;
                return data.result;
            }

            // 6. Check for direct text field
            if (data.text && typeof data.text === 'string') {
                console.log('Found text field:', data.text);
                contentFound = true;
                return data.text;
            }

            // 7. Check for text type object
            if (data.type === 'text' && data.text) {
                console.log('Found text type object:', data.text);
                contentFound = true;
                return data.text;
            }

            // 8. Check for params.data (MCP notification structure)
            if (data.params && data.params.data && typeof data.params.data === 'string') {
                console.log('Found params.data:', data.params.data);
                contentFound = true;
                return data.params.data;
            }

            // 9. Check for any nested content in the entire object structure
            if (!contentFound) {
                console.log('No direct content found, searching through entire object structure...');

                // Helper function to search for content recursively
                const searchForContent = (obj: any, path: string = ''): string[] => {
                    const foundContent: string[] = [];

                    if (typeof obj === 'string' && obj.trim().length > 0) {
                        // Only include content that looks like actual content, not metadata
                        const lowerPath = path.toLowerCase();
                        const isMetadata = lowerPath.includes('method') ||
                                         lowerPath.includes('jsonrpc') ||
                                         lowerPath.includes('sessionid') ||
                                         lowerPath.includes('id') ||
                                         lowerPath.includes('level') ||
                                         path === 'method' ||
                                         path === 'jsonrpc' ||
                                         path === 'sessionId';

                        if (!isMetadata) {
                            console.log(`Found string content at ${path}:`, obj);
                            foundContent.push(obj.trim());
                        }
                    } else if (Array.isArray(obj)) {
                        obj.forEach((item, index) => {
                            const nestedContent = searchForContent(item, `${path}[${index}]`);
                            foundContent.push(...nestedContent);
                        });
                    } else if (typeof obj === 'object' && obj !== null) {
                        Object.keys(obj).forEach(key => {
                            const value = obj[key];
                            const newPath = path ? `${path}.${key}` : key;

                            // Check if this looks like content
                            if (key.toLowerCase().includes('content') ||
                                key.toLowerCase().includes('text') ||
                                key.toLowerCase().includes('message') ||
                                key.toLowerCase().includes('data') ||
                                key.toLowerCase().includes('result')) {
                                console.log(`Found potential content field at ${newPath}:`, value);
                                if (typeof value === 'string' && value.trim().length > 0) {
                                    foundContent.push(value.trim());
                                } else if (typeof value === 'object' && value !== null) {
                                    const nestedContent = searchForContent(value, newPath);
                                    foundContent.push(...nestedContent);
                                }
                            } else {
                                const nestedContent = searchForContent(value, newPath);
                                foundContent.push(...nestedContent);
                            }
                        });
                    }

                    return foundContent;
                };

                const allContent = searchForContent(data);
                if (allContent.length > 0) {
                    console.log('Found content through recursive search:', allContent);
                    return allContent.join('\n\n');
                }
            }

            // Helper function to format content with emojis and proper structure
            const formatContent = (obj: any, path: string = '', depth: number = 0): string => {
                if (depth > 5) return ''; // Prevent infinite recursion

                let content = '';

                if (typeof obj === 'string' && obj.trim().length > 0) {
                    return obj.trim();
                } else if (Array.isArray(obj)) {
                    // Handle arrays - create numbered lists
                    const items = obj.map((item, index) => {
                        if (typeof item === 'string' && item.trim().length > 0) {
                            return `**${index + 1}.** ${item.trim()}`;
                        } else if (typeof item === 'object' && item !== null) {
                            const nestedContent = formatContent(item, `${path}[${index}]`, depth + 1);
                            return nestedContent ? `**${index + 1}.** ${nestedContent}` : '';
                        }
                        return '';
                    }).filter(Boolean);

                    if (items.length > 0) {
                        content += items.join('\n\n');
                    }
                } else if (typeof obj === 'object' && obj !== null) {
                    // Handle objects - create structured content
                    const sections: string[] = [];

                    // Check for common content fields that should be formatted specially
                    const contentFields: Record<string, string> = {
                        'warning': '⚠️ Warnung',
                        'error': '❌ Fehler',
                        'success': '✅ Erfolg',
                        'info': 'ℹ️ Info',
                        'result': '📊 Ergebnis',
                        'recommendations': '🎯 Empfehlungen',
                        'top': '🏆 Top',
                        'building': '🏢 Gebäude',
                        'focus': '🎯 Fokus',
                        'kpi': '📈 KPI',
                        'equipment': '🔧 Ausrüstung',
                        'cost': '💰 Kosten',
                        'impact': '🎯 Impact',
                        'description': '📋 Beschreibung',
                        'summary': '📝 Zusammenfassung',
                        'details': '🔍 Details',
                        'measures': '📊 Maßnahmen',
                        'catalog': '📚 Katalog'
                    };

                    Object.keys(obj).forEach((key: string) => {
                        const value = obj[key];
                        const lowerKey = key.toLowerCase();

                        if (typeof value === 'string' && value.trim().length > 0) {
                            // Check if this is a known content field
                            const emoji = contentFields[lowerKey] || `**${key.charAt(0).toUpperCase() + key.slice(1)}:**`;

                            if (lowerKey === 'warning' || lowerKey === 'error') {
                                sections.push(`${emoji} ${value.trim()}`);
                            } else if (lowerKey === 'result' || lowerKey === 'recommendations' || lowerKey === 'top') {
                                sections.push(`\n${emoji} ${value.trim()}`);
                            } else if (lowerKey === 'description' || lowerKey === 'summary') {
                                sections.push(`📋 **${key.charAt(0).toUpperCase() + key.slice(1)}:** ${value.trim()}`);
                            } else if (lowerKey === 'cost' || lowerKey === 'impact') {
                                sections.push(`🎯 **${key.charAt(0).toUpperCase() + key.slice(1)}:** ${value.trim()}`);
                            } else {
                                sections.push(`${emoji} ${value.trim()}`);
                            }
                        } else if (Array.isArray(value) && value.length > 0) {
                            // Handle arrays within objects
                            const arrayItems = value.map((item, index) => {
                                if (typeof item === 'string' && item.trim().length > 0) {
                                    return `**${index + 1}.** ${item.trim()}`;
                                } else if (typeof item === 'object' && item !== null) {
                                    const nestedContent = formatContent(item, `${path}.${key}[${index}]`, depth + 1);
                                    return nestedContent ? `**${index + 1}.** ${nestedContent}` : '';
                                }
                                return '';
                            }).filter(Boolean);

                            if (arrayItems.length > 0) {
                                const emoji = contentFields[lowerKey] || `**${key.charAt(0).toUpperCase() + key.slice(1)}:**`;
                                sections.push(`\n${emoji}\n\n${arrayItems.join('\n\n')}`);
                            }
                        } else if (typeof value === 'object' && value !== null) {
                            // Handle nested objects
                            const nestedContent = formatContent(value, `${path}.${key}`, depth + 1);
                            if (nestedContent) {
                                const emoji = contentFields[lowerKey] || `**${key.charAt(0).toUpperCase() + key.slice(1)}:**`;
                                sections.push(`\n${emoji}\n\n${nestedContent}`);
                            }
                        }
                    });

                    content = sections.join('\n\n');
                }

                return content;
            };

            // Extract and format content
            formattedContent = formatContent(data);

            // If no formatted content was found, try to extract any readable content
            if (!formattedContent || formattedContent.trim().length === 0) {
                const extractAnyContent = (obj: any): string => {
                    if (typeof obj === 'string' && obj.trim().length > 0) {
                        return obj.trim();
                    } else if (Array.isArray(obj)) {
                        return obj.map((item, index) => {
                            if (typeof item === 'string' && item.trim().length > 0) {
                                return `**${index + 1}.** ${item.trim()}`;
                            } else if (typeof item === 'object' && item !== null) {
                                return extractAnyContent(item);
                            }
                            return '';
                        }).filter(Boolean).join('\n\n');
                    } else if (typeof obj === 'object' && obj !== null) {
                        const items: string[] = [];
                        Object.keys(obj).forEach(key => {
                            const value = obj[key];
                            if (typeof value === 'string' && value.trim().length > 0) {
                                items.push(`**${key.charAt(0).toUpperCase() + key.slice(1)}:** ${value.trim()}`);
                            } else if (typeof value === 'object' && value !== null) {
                                const nestedContent = extractAnyContent(value);
                                if (nestedContent) {
                                    items.push(`**${key.charAt(0).toUpperCase() + key.slice(1)}:**\n${nestedContent}`);
                                }
                            }
                        });
                        return items.join('\n\n');
                    }
                    return '';
                };

                formattedContent = extractAnyContent(data);
            }

            console.log('Final formattedContent:', formattedContent);
            return formattedContent || '**Keine unstrukturierten Daten gefunden**';
        }

        return String(data);
    };

    if (!isOpen) return null;

    return (
        <Dialog
            open
            onOpenChange={(nextOpen) => {
                if (!nextOpen) handleDismiss();
            }}
        >
            <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col overflow-hidden sm:max-w-[1200px]">
                <DialogHeader>
                    <DialogTitle>{t('ui.mcpServerTestTitle')}</DialogTitle>
                </DialogHeader>

                <div className="flex h-[700px] min-h-0 flex-col overflow-hidden">
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <XCircle aria-hidden className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" className="mb-4">
                            <CheckCircle2 aria-hidden className="h-4 w-4" />
                            <AlertDescription>{success}</AlertDescription>
                        </Alert>
                    )}

                    {connecting ? (
                        <div className="flex h-[200px] items-center justify-center">
                            <div className="flex items-center gap-2.5">
                                <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
                                <span>{t('ui.connectingToMcp')}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex h-full max-h-full min-h-0 flex-col overflow-hidden">
                            <div className="flex flex-none flex-col gap-2.5">
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="mcp-server-url">{t('ui.serverUrl')}</Label>
                                    <Input id="mcp-server-url" value={serverURL} readOnly />
                                    <p className="text-xs text-muted-foreground">
                                        {t('ui.notePrefix')}: MCP-Server verwenden typischerweise HTTP/HTTPS-Endpunkte. Gültige Beispiele: https://example.com/mcp, http://localhost:3000/mcp, oder https://api.example.com/v1/mcp
                                    </p>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="mcp-tool-select">{t('ui.availableTools')} ({tools.length})</Label>
                                    <Select
                                        onValueChange={(value) => {
                                            const option = toolOptions.find(candidate => candidate.key === value);
                                            if (option?.data) handleToolSelect(option.data);
                                        }}
                                    >
                                        <SelectTrigger id="mcp-tool-select">
                                            <SelectValue placeholder={t('ui.selectToolToTest')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {toolOptions.map(option => (
                                                <SelectItem key={option.key} value={option.key}>
                                                    {option.text}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {selectedTool && (
                                    <div className="flex flex-col gap-1.5">
                                        <p className="text-sm font-medium">{t('ui.toolLabel')}: {selectedTool.name}</p>
                                        {selectedTool.description && (
                                            <p className="mb-2.5 text-xs text-muted-foreground">
                                                {selectedTool.description}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {parameters.length > 0 && (
                                <div className="mt-2.5 flex h-[400px] max-h-[400px] flex-[0_0_400px] gap-5 overflow-hidden">
                                    {/* Parameters Section - Reduced to 30% */}
                                    <div className="flex h-[400px] max-h-[400px] min-w-0 flex-[0_0_30%] flex-col">
                                        <p className="text-sm font-medium">Parameter</p>
                                        <div className="h-full max-h-full flex-[1_1_auto] overflow-auto">
                                            <div className="flex flex-col gap-2.5">
                                                {parameters.map(param => (
                                                    <div key={param.name}>
                                                        {renderParameterField(param)}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Result Section - Increased to 70% and limited height */}
                                    <div className="flex h-[400px] max-h-[400px] min-w-0 flex-[0_0_70%] flex-col">
                                        <p className="text-sm font-medium">{t('ui.result')}</p>
                                        <div className="h-full max-h-full flex-[1_1_auto] overflow-auto">
                                            {loading ? (
                                                <div className="flex h-full flex-col items-center justify-center">
                                                    <div className="flex items-center gap-4">
                                                        <Loader2 aria-hidden className="h-7 w-7 animate-spin" />
                                                        <div>
                                                            <div className="mb-1 text-base font-bold">
                                                                {t('ui.executingTool')}
                                                            </div>
                                                            <div className="mb-1 text-xs text-muted-foreground">
                                                                {t('ui.waitingForResponse')}
                                                            </div>
                                                            {executionTime > 0 && (
                                                                <div className="text-xs text-muted-foreground">
                                                                    {t('ui.runtimeSeconds', { seconds: executionTime })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : result ? (
                                                <div className="flex h-full flex-row gap-2.5">
                                                    {/* Structured Content */}
                                                    <div className="flex h-full min-w-0 flex-[1_1_50%] flex-col">
                                                        <p className="mb-1 text-sm font-medium">{t('ui.structuredDataJson')}</p>
                                                        <div className="h-[350px] overflow-hidden rounded-md border">
                                                            <Textarea
                                                                aria-label={t('ui.structuredDataJson')}
                                                                rows={12}
                                                                value={JSON.stringify(result, null, 2)}
                                                                readOnly
                                                                className="h-full max-h-full resize-none overflow-auto rounded-none border-none bg-muted font-mono text-xs shadow-none focus-visible:ring-0"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Unstructured Content */}
                                                    <div className="flex h-full min-w-0 flex-[1_1_50%] flex-col">
                                                        <p className="mb-1 text-sm font-medium">{t('ui.unstructuredData')}</p>
                                                        <div className="relative h-[350px] overflow-auto rounded-md border bg-muted p-3 text-xs leading-snug">
                                                            <div className="absolute inset-3 overflow-auto">
                                                                <div className="mb-2.5 rounded-md bg-accent p-2 text-xs">
                                                                    <strong>Debug Info:</strong><br/>
                                                                    Raw result type: {typeof result}<br/>
                                                                    Raw result keys: {result ? Object.keys(result).join(', ') : 'null'}<br/>
                                                                    Extracted content length: {extractUnstructuredContent(result).length}<br/>
                                                                    Extracted content preview: {extractUnstructuredContent(result).substring(0, 100)}...
                                                                </div>
                                                                <div className="rounded-md bg-background p-2 text-xs">
                                                                    <strong>Markdown Content:</strong><br/>
                                                                    <ReactMarkdown
                                                                        components={{
                                                                            p: ({children}) => <p className="my-2 leading-snug">{children}</p>,
                                                                            strong: ({children}) => <strong className="font-bold">{children}</strong>,
                                                                            em: ({children}) => <em className="italic">{children}</em>,
                                                                            h1: ({children}) => <h1 className="mb-2 mt-3 text-base font-bold">{children}</h1>,
                                                                            h2: ({children}) => <h2 className="mb-1.5 mt-2.5 text-sm font-bold">{children}</h2>,
                                                                            h3: ({children}) => <h3 className="mb-1 mt-2 text-xs font-bold">{children}</h3>,
                                                                            ul: ({children}) => <ul className="my-2 list-disc pl-5">{children}</ul>,
                                                                            ol: ({children}) => <ol className="my-2 list-decimal pl-5">{children}</ol>,
                                                                            li: ({children}) => <li className="my-1">{children}</li>,
                                                                            code: ({children}) => <code className="rounded-sm bg-muted px-1 py-0.5 font-mono">{children}</code>,
                                                                            pre: ({children}) => <pre className="overflow-auto rounded-md bg-muted p-2 text-xs">{children}</pre>
                                                                        }}
                                                                    >
                                                                        {extractUnstructuredContent(result)}
                                                                    </ReactMarkdown>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex h-full items-center justify-center rounded-md border border-dashed bg-muted">
                                                    <div className="text-center text-muted-foreground">
                                                        <div className="mb-2 text-sm">
                                                            {t('ui.noResultAvailable')}
                                                        </div>
                                                        <div className="text-xs">
                                                            {t('ui.runToolToSeeResult')}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {parameters.length === 0 && selectedTool && (
                                <div className="mt-2.5 flex h-[400px] max-h-[400px] flex-[1_1_auto] flex-col overflow-hidden">
                                    <p className="text-sm font-medium">{t('ui.result')}</p>
                                    <div className="h-full max-h-full flex-[1_1_auto] overflow-auto">
                                        {loading ? (
                                            <div className="flex h-full flex-col items-center justify-center">
                                                <div className="flex items-center gap-4">
                                                    <Loader2 aria-hidden className="h-7 w-7 animate-spin" />
                                                    <div>
                                                        <div className="mb-1 text-base font-bold">
                                                            {t('ui.executingTool')}
                                                        </div>
                                                        <div className="mb-1 text-xs text-muted-foreground">
                                                            {t('ui.waitingForResponse')}
                                                        </div>
                                                        {executionTime > 0 && (
                                                            <div className="text-xs text-muted-foreground">
                                                                {t('ui.runtimeSeconds', { seconds: executionTime })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : result ? (
                                            <div className="flex h-full flex-row gap-2.5">
                                                {/* Structured Content */}
                                                <div className="flex h-full min-w-0 flex-[1_1_50%] flex-col">
                                                    <p className="mb-1 text-sm font-medium">{t('ui.structuredDataJson')}</p>
                                                    <div className="h-[350px] overflow-hidden rounded-md border">
                                                        <Textarea
                                                            aria-label={t('ui.structuredDataJson')}
                                                            rows={12}
                                                            value={JSON.stringify(result, null, 2)}
                                                            readOnly
                                                            className="h-full max-h-full resize-none overflow-auto rounded-none border-none bg-muted font-mono text-xs shadow-none focus-visible:ring-0"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Unstructured Content */}
                                                <div className="flex h-full min-w-0 flex-[1_1_50%] flex-col">
                                                    <p className="mb-1 text-sm font-medium">{t('ui.unstructuredData')}</p>
                                                    <div className="relative h-[350px] overflow-auto rounded-md border bg-muted p-3 text-xs leading-snug">
                                                        <div className="absolute inset-3 overflow-auto">
                                                            <div className="mb-2.5 rounded-md bg-accent p-2 text-xs">
                                                                <strong>Debug Info:</strong><br/>
                                                                Raw result type: {typeof result}<br/>
                                                                Raw result keys: {result ? Object.keys(result).join(', ') : 'null'}<br/>
                                                                Extracted content length: {extractUnstructuredContent(result).length}<br/>
                                                                Extracted content preview: {extractUnstructuredContent(result).substring(0, 100)}...
                                                            </div>
                                                            <div className="rounded-md bg-background p-2 text-xs">
                                                                <strong>Markdown Content:</strong><br/>
                                                                <ReactMarkdown
                                                                    components={{
                                                                        p: ({children}) => <p className="my-2 leading-snug">{children}</p>,
                                                                        strong: ({children}) => <strong className="font-bold">{children}</strong>,
                                                                        em: ({children}) => <em className="italic">{children}</em>,
                                                                        h1: ({children}) => <h1 className="mb-2 mt-3 text-base font-bold">{children}</h1>,
                                                                        h2: ({children}) => <h2 className="mb-1.5 mt-2.5 text-sm font-bold">{children}</h2>,
                                                                        h3: ({children}) => <h3 className="mb-1 mt-2 text-xs font-bold">{children}</h3>,
                                                                        ul: ({children}) => <ul className="my-2 list-disc pl-5">{children}</ul>,
                                                                        ol: ({children}) => <ol className="my-2 list-decimal pl-5">{children}</ol>,
                                                                        li: ({children}) => <li className="my-1">{children}</li>,
                                                                        code: ({children}) => <code className="rounded-sm bg-muted px-1 py-0.5 font-mono">{children}</code>,
                                                                        pre: ({children}) => <pre className="overflow-auto rounded-md bg-muted p-2 text-xs">{children}</pre>
                                                                    }}
                                                                >
                                                                    {extractUnstructuredContent(result)}
                                                                </ReactMarkdown>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex h-full items-center justify-center rounded-md border border-dashed bg-muted">
                                                <div className="text-center text-muted-foreground">
                                                    <div className="mb-2 text-sm">
                                                        {t('ui.noResultAvailable')}
                                                    </div>
                                                    <div className="text-xs">
                                                        {t('ui.runToolToSeeResult')}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter className="shrink-0 gap-2 pt-4 sm:gap-0">
                    {error && !connecting && (
                        <div className="flex min-w-0 flex-col gap-2.5">
                            <Button type="button" variant="outline" onClick={connectToServer}>
                                {t('ui.retryConnection')}
                            </Button>
                            <Separator />
                            <div className="text-xs text-muted-foreground">
                                <strong>{t('ui.alternativeTestMethods')}</strong><br />
                                {t('ui.useAnthropicInspector')}<br />
                                {t('ui.makeDirectHttpRequests', { url: transformedURL || serverURL })}<br />
                                {t('ui.useServerSideClient')}
                            </div>
                        </div>
                    )}
                    {selectedTool && (
                        <Button type="button" onClick={executeTool} disabled={loading}>
                            {loading ? t('ui.executingEllipsis') : t('ui.executeTool')}
                        </Button>
                    )}
                    <Button type="button" variant="outline" onClick={handleDismiss}>
                        {t('ui.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
